import { Link, useCanGoBack, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  CircleArrowLeft,
  Download,
  FileText,
  Loader2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  downloadImportDraftCsv,
  downloadImportErrorsCsv,
  useImportJobAuditList,
  useImportJobDetail,
} from '~entities/import-job/api/import-job-api';
import type {
  ImportJobStatus,
  ImportJobSummary,
} from '~entities/import-job/api/types';

import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

const STATUS_LABELS: Record<ImportJobStatus, string> = {
  draft: 'Draft',
  queued: 'Queued',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Discarded',
};

const STATUS_VARIANT: Record<
  ImportJobStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  draft: 'secondary',
  queued: 'outline',
  processing: 'default',
  completed: 'default',
  failed: 'destructive',
  cancelled: 'outline',
};

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatPlatform(value: string) {
  return value.replace(/_/g, ' ');
}

function filesSummary(job: ImportJobSummary) {
  const files: string[] = [];
  if (job.hasClassCsv) {
    files.push(job.classCsvFilename ?? 'classes.csv');
  }
  if (job.hasParentCsv) {
    files.push(job.parentCsvFilename ?? 'families.csv');
  }
  if (job.stagedImageCount > 0) {
    files.push(`${job.stagedImageCount} photo(s)`);
  }
  return files.length > 0 ? files.join(', ') : 'No files saved';
}

function resultsSummary(job: ImportJobSummary) {
  if (job.status === 'draft') {
    return `Step ${job.wizardStepReached} · not launched`;
  }
  if (job.status === 'cancelled') {
    return 'Draft discarded';
  }
  const parts: string[] = [];
  if (job.classesProcessed != null && job.classesProcessed > 0) {
    parts.push(`${job.classesProcessed} class(es)`);
  }
  if (job.parentsProcessed != null && job.parentsProcessed > 0) {
    parts.push(`${job.parentsProcessed} famil(ies)`);
  }
  if (job.emailsSent != null && job.emailsSent > 0) {
    parts.push(`${job.emailsSent} email(s)`);
  }
  if (job.errorCount != null && job.errorCount > 0) {
    parts.push(`${job.errorCount} issue(s)`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'No records created';
}

async function downloadCsvBlob(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function ImportAuditDetail({ jobId }: { jobId: string }) {
  const { data: job, isLoading } = useImportJobDetail(jobId, true);

  const handleDownloadDraft = async (dataset: 'classes' | 'parents') => {
    try {
      const content = await downloadImportDraftCsv(jobId, dataset);
      const filename =
        dataset === 'classes'
          ? (job?.classCsvFilename ?? 'classes.csv')
          : (job?.parentCsvFilename ?? 'families.csv');
      await downloadCsvBlob(content, filename);
    } catch {
      toast.error('File no longer available for this session');
    }
  };

  const handleDownloadErrors = async (dataset: 'classes' | 'parents') => {
    try {
      const blob = await downloadImportErrorsCsv(jobId, dataset);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `import-${dataset}-errors.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download error file');
    }
  };

  if (isLoading || !job) {
    return (
      <div className='flex items-center gap-2 py-4 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading session…
      </div>
    );
  }

  return (
    <div className='space-y-4 border-t pt-4'>
      <div className='grid gap-3 text-sm sm:grid-cols-2'>
        <div>
          <div className='text-muted-foreground'>Import session ID</div>
          <div className='font-mono text-xs'>{job.jobId}</div>
        </div>
        {job.correlationId && (
          <div>
            <div className='text-muted-foreground'>Correlation ID</div>
            <div className='font-mono text-xs'>{job.correlationId}</div>
          </div>
        )}
        <div>
          <div className='text-muted-foreground'>Welcome emails</div>
          <div>{job.welcomeEmailEnabled ? 'Enabled' : 'Disabled'}</div>
        </div>
        <div>
          <div className='text-muted-foreground'>Session closed</div>
          <div>{formatDate(job.sessionClosedAt)}</div>
        </div>
      </div>

      <div>
        <div className='mb-2 text-sm font-medium'>Files used</div>
        <div className='flex flex-wrap gap-2'>
          {job.hasClassCsv && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => void handleDownloadDraft('classes')}
            >
              <Download className='mr-2 size-3.5' />
              {job.classCsvFilename ?? 'classes.csv'}
            </Button>
          )}
          {job.hasParentCsv && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => void handleDownloadDraft('parents')}
            >
              <Download className='mr-2 size-3.5' />
              {job.parentCsvFilename ?? 'families.csv'}
            </Button>
          )}
          {(job.stagedImages?.length ?? 0) > 0 && (
            <Badge variant='secondary'>
              {job.stagedImages?.length} staged photo(s)
            </Badge>
          )}
          {!job.hasClassCsv && !job.hasParentCsv && (
            <span className='text-sm text-muted-foreground'>
              No CSV files retained
            </span>
          )}
        </div>
      </div>

      {(job.status === 'completed' || job.status === 'failed') && (
        <div>
          <div className='mb-2 text-sm font-medium'>Import results</div>
          <dl className='grid grid-cols-2 gap-2 text-sm sm:grid-cols-4'>
            <div>
              <dt className='text-muted-foreground'>Classes</dt>
              <dd>
                {job.classesProcessed} / {job.totalClasses}
              </dd>
            </div>
            <div>
              <dt className='text-muted-foreground'>Photos</dt>
              <dd>
                {job.imagesProcessed} / {job.totalImages}
              </dd>
            </div>
            <div>
              <dt className='text-muted-foreground'>Families</dt>
              <dd>
                {job.parentsProcessed} / {job.totalParents}
              </dd>
            </div>
            <div>
              <dt className='text-muted-foreground'>Emails sent</dt>
              <dd>{job.emailsSent}</dd>
            </div>
          </dl>
          {(job.errors?.length ?? 0) > 0 && (
            <div className='mt-3 flex flex-wrap gap-2'>
              {job.errors?.some((e) => e.phase === 'classes') && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => void handleDownloadErrors('classes')}
                >
                  Class issues (.csv)
                </Button>
              )}
              {job.errors?.some((e) => e.phase === 'parents') && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => void handleDownloadErrors('parents')}
                >
                  Family issues (.csv)
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {(job.sessionEvents?.length ?? 0) > 0 && (
        <div>
          <div className='mb-2 text-sm font-medium'>
            Session log ({job.sessionEvents?.length} events)
          </div>
          <ul className='max-h-64 space-y-2 overflow-y-auto rounded-md border p-3 text-sm'>
            {job.sessionEvents?.map((event, index) => (
              <li
                key={`${event.type}-${event.at}-${index}`}
                className='flex flex-wrap justify-between gap-2'
              >
                <span>{event.message}</span>
                <span className='shrink-0 text-xs text-muted-foreground'>
                  {formatDate(event.at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AuditLogRow({
  job,
  expanded,
  onToggle,
}: {
  job: ImportJobSummary;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Card>
      <CardHeader className='pb-3'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <CardTitle className='text-base capitalize'>
                {formatPlatform(job.sourcePlatform)}
              </CardTitle>
              <Badge variant={STATUS_VARIANT[job.status]}>
                {STATUS_LABELS[job.status]}
              </Badge>
              {job.sessionClosedAt && (
                <Badge variant='outline'>Closed</Badge>
              )}
            </div>
            <CardDescription className='mt-1'>
              {filesSummary(job)} · {resultsSummary(job)}
            </CardDescription>
            <p className='mt-1 text-xs text-muted-foreground'>
              Started {formatDate(job.createdAt)} · Updated{' '}
              {formatDate(job.updatedAt)}
            </p>
          </div>
          <Button variant='ghost' size='sm' onClick={onToggle}>
            {expanded ? (
              <>
                <ChevronDown className='mr-1 size-4' />
                Hide
              </>
            ) : (
              <>
                <ChevronRight className='mr-1 size-4' />
                Details
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className='pt-0'>
          <ImportAuditDetail jobId={job.jobId} />
        </CardContent>
      )}
    </Card>
  );
}

export function ImportAuditLogPage() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const [statusFilter, setStatusFilter] = useState<ImportJobStatus | 'all'>(
    'all',
  );
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  const { data: jobs, isLoading, error, refetch } = useImportJobAuditList({
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  return (
    <div className='mx-auto max-w-4xl space-y-6 p-6'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='space-y-1'>
          {canGoBack ? (
            <Button
              variant='ghost'
              className='-ml-2 flex h-auto items-center gap-2 px-2 py-1 [&_svg]:size-5'
              onClick={() => {
                router.history.back();
              }}
            >
              <CircleArrowLeft />
              <h1 className='text-2xl font-semibold'>Import history</h1>
            </Button>
          ) : (
            <Link
              to='/import-data'
              className='flex w-max items-center gap-2 text-muted-foreground transition-colors hover:text-foreground'
            >
              <CircleArrowLeft className='size-5' />
              <h1 className='text-2xl font-semibold text-foreground'>
                Import history
              </h1>
            </Link>
          )}
          <p className='text-muted-foreground'>
            Audit log of import sessions — files used, actions taken, and
            results.
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Button variant='outline' asChild>
            <Link to='/import-data'>
              <FileText className='mr-2 size-4' />
              Import Wizard
            </Link>
          </Button>
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <Select
          value={statusFilter}
          onValueChange={(value) =>
            setStatusFilter(value as ImportJobStatus | 'all')
          }
        >
          <SelectTrigger className='w-44'>
            <SelectValue placeholder='Filter by status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All statuses</SelectItem>
            <SelectItem value='draft'>Draft</SelectItem>
            <SelectItem value='completed'>Completed</SelectItem>
            <SelectItem value='failed'>Failed</SelectItem>
            <SelectItem value='cancelled'>Discarded</SelectItem>
            <SelectItem value='processing'>Processing</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => {
            void refetch();
            void queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
          }}
        >
          Refresh
        </Button>
      </div>

      {isLoading && (
        <div className='flex flex-col items-center justify-center gap-3 py-16'>
          <Loader2 className='size-8 animate-spin text-muted-foreground' />
          <p className='text-muted-foreground'>Loading import history…</p>
        </div>
      )}

      {error && (
        <Card>
          <CardContent className='py-8 text-center text-destructive'>
            Could not load import history.
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && (jobs?.length ?? 0) === 0 && (
        <Card>
          <CardContent className='py-12 text-center text-muted-foreground'>
            No import sessions yet. Start one from Import Wizard.
          </CardContent>
        </Card>
      )}

      <div className='space-y-3'>
        {jobs?.map((job) => (
          <AuditLogRow
            key={job.jobId}
            job={job}
            expanded={expandedJobId === job.jobId}
            onToggle={() =>
              setExpandedJobId((current) =>
                current === job.jobId ? null : job.jobId,
              )
            }
          />
        ))}
      </div>
    </div>
  );
}
